import { FontWeight } from '@pasteltech/theme-provider'
import { memo, useCallback, useState } from 'react'
import ReactMarkDown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { styled } from 'styled-components'
import { AddIcon, RemoveIcon } from '../../svg-components'
import { DeepNullable, ScreenQuery } from '../../utilities'

const Background = styled.div`
  background-color: ${({ theme }) => theme.colors.container.lowest};
  background-image: url('/images/services-page-background.png');
  background-position: right top;
  background-repeat: no-repeat;
  background-size: cover;
`
const ListContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 90%;
  margin: 0 auto;

  padding: ${({ theme }) => theme.sizes.spacings.xxxxxl}px 0px;
  gap: ${({ theme }) => theme.sizes.spacings.lg}px;
`
const PlanContainer = styled.div`
  box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ theme }) => theme.sizes.spacings.xl}px;
  background-color: ${({ theme }) => theme.colors.container.lowest};
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
`

const ServicesContainer = styled.div`
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  ${ScreenQuery.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
  ${ScreenQuery.mobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`
const ServiceContainer = styled.div`
  min-height: 150px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes.spacings.xs}px;

  box-sizing: border-box;
  padding: ${({ theme }) => theme.sizes.spacings.lg}px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius.xs}px;
  background: ${({ theme }) => theme.colors.container.medium};
`
const ButtonWrapper = styled.div``
const StyledButton = styled.button`
  border-radius: 50%;
  &:hover {
    cursor: pointer;
  }
  background-color: ${({ theme }) => theme.colors.container.lowest};
  padding: ${({ theme }) => theme.sizes.spacings.xs}px;
  border: 1px solid ${({ theme }) => theme.colors.secondary.main};
`
const PlanTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.high};
  font-size: ${({ theme }) => theme.sizes.fontSize.heading3}px;
  font-weight: ${FontWeight.bold};
`
const PlanDescription = styled.div`
  color: ${({ theme }) => theme.colors.text.high};
  font-size: ${({ theme }) => theme.sizes.fontSize.title}px;
  font-weight: ${FontWeight.bold};
  p {
    margin: 0;
    word-break: break-all;
  }
`
const ServiceTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.high};
  font-size: ${({ theme }) => theme.sizes.fontSize.title}px;
  font-weight: ${FontWeight.bold};
`
const ServiceDescription = styled.div`
  color: ${({ theme }) => theme.colors.text.medium};
  font-size: ${({ theme }) => theme.sizes.fontSize.button}px;
  font-weight: ${FontWeight.medium};
  p {
    margin: 0;
    word-break: break-all;
  }
`
const ButtonContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.sizes.spacings.xs}px;
`
const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`
const PlanDetailContainer = styled.div<{ $isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? '1fr' : '0fr')};
  overflow: hidden;
  transition: grid-template-rows 0.3s ease-in-out;
  width: 100%;
`

const PlanDetailContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.sizes.spacings.xl}px;
  min-height: 0;
`
type Service = DeepNullable<{
  id: string
  title: string
  description: string
}>
type Plan = DeepNullable<{
  id: string
  title: string
  description: string
  services: Service[]
}>

type PlanServiceListProps = {
  plans: Plan[] | null
}
export const PlanServiceList = memo(({ plans }: PlanServiceListProps) => {
  const [expandedPlansId, setExpandedPlansId] = useState<string[]>([])

  const handleButtonOnClick = useCallback(
    (plan: Plan) => {
      if (plan.id === null) {
        // This should never happen unless missing id in graphql query
        throw new Error('Plan id is missing')
      }
      if (expandedPlansId.includes(plan.id)) {
        setExpandedPlansId(expandedPlansId.filter((id) => id !== plan.id))
      } else {
        setExpandedPlansId([...expandedPlansId, plan.id])
      }
    },
    [expandedPlansId],
  )
  return (
    <Background>
      <ListContainer>
        {plans?.map((plan) => {
          // This should never happen unless missing id in graphql query
          const isExpanded = plan?.id
            ? expandedPlansId.includes(plan.id)
            : false

          return (
            <PlanContainer key={plan.id}>
              <TitleContainer>
                <PlanTitle>{plan.title}</PlanTitle>
                <ButtonWrapper>
                  <StyledButton onClick={() => handleButtonOnClick(plan)}>
                    <ButtonContent>
                      {isExpanded ? <RemoveIcon /> : <AddIcon />}
                    </ButtonContent>
                  </StyledButton>
                </ButtonWrapper>
              </TitleContainer>
              <PlanDetailContainer $isExpanded={isExpanded}>
                <PlanDetailContent>
                  <PlanDescription>
                    <ReactMarkDown rehypePlugins={[rehypeRaw]}>
                      {plan.description}
                    </ReactMarkDown>
                  </PlanDescription>
                  <ServicesContainer>
                    {plan?.services?.map((service) => (
                      <ServiceContainer key={service?.id}>
                        <ServiceTitle>{service?.title}</ServiceTitle>
                        <ServiceDescription>
                          <ReactMarkDown rehypePlugins={[rehypeRaw]}>
                            {service?.description}
                          </ReactMarkDown>
                        </ServiceDescription>
                      </ServiceContainer>
                    ))}
                  </ServicesContainer>
                </PlanDetailContent>
              </PlanDetailContainer>
            </PlanContainer>
          )
        })}
      </ListContainer>
    </Background>
  )
})

PlanServiceList.displayName = 'PlanServiceList'
